/**
 * SiteTopologyTree — hierarchical render of one site's devices.
 *
 * 4 horizontal swimlanes: WAN → Appliance → Switches → Access Points. Each
 * lane is a row of cards. We draw simple connector lines between lanes via
 * absolutely-positioned divs so the visual matches the stakeholder mock
 * without paying for react-flow's runtime here.
 *
 * Each lane caps the visible cards at `previewLimit` (default 12) and
 * shows a "+N more" overflow card with a status dot summary.
 */
import { Cloud, Layers, Network, Wifi } from "lucide-react";
import {
  Fragment,
  type MutableRefObject,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { SiteDevice, SiteDeviceStatus } from "@/api/schemas/sites";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

export interface SiteTopologyTreeProps {
  devices: SiteDevice[];
  statusCounts?: Partial<Record<SiteDeviceStatus, number>>;
  selectedStatuses?: Set<SiteDeviceStatus>;
  onToggleStatus?: (status: SiteDeviceStatus) => void;
  onResetStatuses?: () => void;
  /** Render without outer Card wrapper (used when nested inside another panel). */
  embedded?: boolean;
  /** When true, hides the "Network topology" title header. */
  hideTitle?: boolean;
  /** Max cards rendered per lane before collapsing to "+N more". */
  previewLimit?: number;
}

const STATUS_DOT: Record<SiteDeviceStatus, string> = {
  online: "bg-mint",
  alerting: "bg-peach",
  offline: "bg-rose",
  dormant: "bg-fg-mute/60",
};

type DetailPlacement = "above" | "below";

function displayName(device: SiteDevice): string {
  const raw = (device.name || "").trim();
  if (
    !raw ||
    raw.toLowerCase() === "(unnamed)" ||
    raw.toLowerCase() === "unnamed"
  ) {
    return device.serial;
  }
  return raw;
}

function TopologyDeviceCard({
  d,
  laneId,
  laneIcon,
  activeDetailKey,
  setActiveDetailKey,
  detailPlacementByKey,
  cardRefs,
  popupRefs,
}: {
  d: SiteDevice;
  laneId: string;
  laneIcon: ReactNode;
  activeDetailKey: string | null;
  setActiveDetailKey: (
    v: string | null | ((prev: string | null) => string | null),
  ) => void;
  detailPlacementByKey: Record<string, DetailPlacement>;
  cardRefs: MutableRefObject<Map<string, HTMLDivElement | null>>;
  popupRefs: MutableRefObject<Map<string, HTMLDivElement | null>>;
}) {
  const nodeKey = `${laneId}-${d.serial}`;
  return (
    <div
      className="group relative"
      ref={(el) => {
        cardRefs.current.set(nodeKey, el);
      }}
      onMouseEnter={() => setActiveDetailKey(nodeKey)}
      onMouseLeave={() =>
        setActiveDetailKey((cur) => (cur === nodeKey ? null : cur))
      }
    >
      <div
        className={cn(
          "flex items-center gap-2 rounded-xl border bg-surface px-3 py-2.5",
          d.status === "online" && "border-mint/30",
          d.status === "alerting" && "border-peach/30",
          d.status === "offline" && "border-rose/30",
          d.status === "dormant" && "border-line",
        )}
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-aurora-soft text-violet">
          {laneIcon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span
              className={cn(
                "h-1.5 w-1.5 shrink-0 rounded-full",
                STATUS_DOT[d.status],
              )}
            />
            <span className="truncate text-[11px] font-semibold text-fg">
              {displayName(d)}
            </span>
          </div>
          <div className="truncate font-mono text-[10px] text-fg-mute">
            {d.model}
            {d.lanIp ? ` · ${d.lanIp}` : ""}
          </div>
        </div>
      </div>

      {(() => {
        const detailRows: Array<{ label: string; value: string }> = [];
        const pushRow = (label: string, value: string | null | undefined) => {
          if (!value) return;
          const normalized = value.trim();
          if (!normalized) return;
          detailRows.push({ label, value: normalized });
        };
        pushRow("Serial", d.serial);
        pushRow("MAC", d.macAddress ?? null);
        pushRow("Address", d.address ?? null);
        pushRow("Public IP", d.publicIp ?? null);
        pushRow("LAN IP", d.lanIp ?? null);
        pushRow("Tags", (d.tags ?? []).join(", "));
        pushRow("Notes", d.notes ?? null);
        if (detailRows.length === 0) return null;
        const placement = detailPlacementByKey[nodeKey] ?? "below";
        if (activeDetailKey !== nodeKey) return null;
        return (
          <div
            ref={(el) => {
              popupRefs.current.set(nodeKey, el);
            }}
            className={cn(
              "pointer-events-none absolute left-0 right-0 z-20 rounded-lg border border-line bg-surface/95 px-2.5 py-2 text-[10px] shadow-xl backdrop-blur-sm",
              placement === "above" ? "bottom-full mb-1" : "top-full mt-1",
            )}
          >
            <div className="mb-1 font-mono text-[10px] text-fg">
              {displayName(d)}
            </div>
            <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-fg-dim">
              {detailRows.map((row) => (
                <Fragment key={row.label}>
                  <span className="text-fg-mute">{row.label}</span>
                  <span
                    className={cn(
                      "truncate",
                      row.label === "Notes" && "line-clamp-2 whitespace-normal",
                      ["Serial", "MAC", "Public IP", "LAN IP"].includes(
                        row.label,
                      ) && "font-mono",
                    )}
                  >
                    {row.value}
                  </span>
                </Fragment>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export function SiteDiagram({
  devices,
  statusCounts,
  selectedStatuses,
  onToggleStatus,
  onResetStatuses,
  embedded = false,
  hideTitle = false,
  previewLimit = 12,
}: SiteTopologyTreeProps) {
  const lanesAnimatedRef = useUiAutoAnimate();
  const [laneVisibleCount, setLaneVisibleCount] = useState<
    Record<string, number>
  >({});
  const [activeDetailKey, setActiveDetailKey] = useState<string | null>(null);
  const [detailPlacementByKey, setDetailPlacementByKey] = useState<
    Record<string, DetailPlacement>
  >({});
  const cardRefs = useRef<Map<string, HTMLDivElement | null>>(null!);
  if (cardRefs.current == null) { cardRefs.current = new Map(); }
  const popupRefs = useRef<Map<string, HTMLDivElement | null>>(null!);
  if (popupRefs.current == null) { popupRefs.current = new Map(); }


  useLayoutEffect(() => {
    if (!activeDetailKey) return;
    const cardEl = cardRefs.current.get(activeDetailKey);
    const popupEl = popupRefs.current.get(activeDetailKey);
    if (!cardEl || !popupEl) return;

    const rect = cardEl.getBoundingClientRect();
    const popupHeight = popupEl.offsetHeight || popupEl.scrollHeight;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const placement: DetailPlacement =
      spaceBelow < popupHeight + 12 && spaceAbove > spaceBelow
        ? "above"
        : "below";

    setDetailPlacementByKey((prev) =>
      prev[activeDetailKey] === placement
        ? prev
        : { ...prev, [activeDetailKey]: placement },
    );
  }, [activeDetailKey, devices, previewLimit]);

  const wan = devices.filter((d) => d.deviceClass === "wan");
  const appliances = devices.filter((d) => d.deviceClass === "appliance");
  const switches = devices.filter((d) => d.deviceClass === "switch");
  const aps = devices.filter((d) => d.deviceClass === "wireless");

  function showMoreInLane(laneId: string) {
    setLaneVisibleCount((prev) => {
      const current = prev[laneId] ?? previewLimit;
      return { ...prev, [laneId]: current + previewLimit };
    });
  }

  const lanes: Array<{
    id: string;
    label: string;
    icon: React.ReactNode;
    devices: SiteDevice[];
    /** Show even when empty (e.g. WAN row is always there). */
    alwaysShow?: boolean;
  }> = [
    {
      id: "wan",
      label: `INTERNET WAN (${wan.length})`,
      icon: <Cloud size={14} />,
      devices: wan,
      alwaysShow: false,
    },
    {
      id: "appliance",
      label: `APPLIANCE (${appliances.length})`,
      icon: <Cloud size={14} />,
      devices: appliances,
    },
    {
      id: "switch",
      label: `SWITCHES (${switches.length})`,
      icon: <Network size={14} />,
      devices: switches,
    },
    {
      id: "ap",
      label: `ACCESS POINTS (${aps.length})`,
      icon: <Wifi size={14} />,
      devices: aps,
    },
  ];

  const content = (
    <>
      {!hideTitle ? (
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.12em] text-fg-mute">
              Network topology
            </div>
          </div>
          <div className="font-mono text-[11px] text-fg-mute">
            {devices.length} devices
          </div>
        </div>
      ) : null}

      <div ref={lanesAnimatedRef} className="flex flex-col gap-7">
        {(() => {
          let renderedLaneCount = 0;
          return lanes.map((lane, _) => {
            if (!lane.alwaysShow && lane.devices.length === 0) return null;
            const visibleCount = laneVisibleCount[lane.id] ?? previewLimit;
            const visible = lane.devices.slice(0, visibleCount);
            const hidden = lane.devices.length - visible.length;
            // Gather hidden status totals so the +N card carries information.
            const hiddenByStatus: Partial<Record<SiteDeviceStatus, number>> =
              {};
            for (const d of lane.devices.slice(visibleCount)) {
              hiddenByStatus[d.status] = (hiddenByStatus[d.status] ?? 0) + 1;
            }
            const isFirstVisibleLane = renderedLaneCount === 0;
            renderedLaneCount++;
            return (
              <div key={lane.id} className="relative">
                {/* Lane label */}
                <div className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
                  <span className="text-violet">{lane.icon}</span>
                  {lane.label}
                </div>

                {/* Connector to the lane above (skip for the first visible lane). */}
                {!isFirstVisibleLane ? (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-7 left-1/2 h-7 w-px"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(0deg, rgba(125,108,255,0.45) 0 4px, transparent 4px 8px)",
                    }}
                  />
                ) : null}

                {/* Cards */}
                <div
                  className="grid gap-3"
                  style={{
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(180px, 1fr))",
                  }}
                >
                  {visible.map((d) => (
                    <TopologyDeviceCard
                      key={`${lane.id}-${d.serial}`}
                      d={d}
                      laneId={lane.id}
                      laneIcon={lane.icon}
                      activeDetailKey={activeDetailKey}
                      setActiveDetailKey={setActiveDetailKey}
                      detailPlacementByKey={detailPlacementByKey}
                      cardRefs={cardRefs}
                      popupRefs={popupRefs}
                    />
                  ))}

                  {hidden > 0 ? (
                    <button
                      type="button"
                      onClick={() => showMoreInLane(lane.id)}
                      className="flex items-center gap-2 rounded-xl border border-dashed border-line bg-surface-tint/40 px-3 py-2.5 text-left transition-colors hover:border-violet/50 hover:bg-violet/10"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-line/30 text-fg-mute">
                        <Layers size={12} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-semibold text-fg">
                          Show +{Math.min(hidden, previewLimit)} more
                        </div>
                        <div className="flex items-center gap-1.5 font-mono text-[10px] text-fg-mute">
                          {(
                            [
                              "online",
                              "alerting",
                              "offline",
                              "dormant",
                            ] as SiteDeviceStatus[]
                          ).map((s) =>
                            hiddenByStatus[s] ? (
                              <span
                                key={s}
                                className="inline-flex items-center gap-0.5"
                              >
                                <span
                                  className={cn(
                                    "inline-block h-1.5 w-1.5 rounded-full",
                                    STATUS_DOT[s],
                                  )}
                                />
                                {hiddenByStatus[s]}
                              </span>
                            ) : null,
                          )}
                        </div>
                      </div>
                    </button>
                  ) : null}
                </div>
              </div>
            );
          });
        })()}
      </div>

      {/* Legend */}
      <div
        className={cn(
          "mt-5 flex flex-wrap items-center gap-2 pt-3 text-[11px] text-fg-mute",
          !embedded && "border-t border-line",
        )}
      >
        {(
          [
            ["online", "Online", "bg-mint"],
            ["alerting", "Alerting", "bg-peach"],
            ["offline", "Offline", "bg-rose"],
            ["dormant", "Dormant", "bg-fg-mute/60"],
          ] as const
        ).map(([status, label, dot]) => {
          const count =
            statusCounts?.[status] ??
            devices.filter((d) => d.status === status).length;
          if (!count) return null;
          const active = selectedStatuses?.has(status) ?? false;
          return (
            <button
              key={status}
              type="button"
              onClick={() => onToggleStatus?.(status)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-semibold transition-colors",
                active
                  ? "border-violet/50 bg-violet/15 text-violet"
                  : "border-line bg-surface text-fg-dim hover:text-fg",
              )}
            >
              <span className={cn("inline-block h-2 w-2 rounded-full", dot)} />
              {label}
              <span className="font-mono text-[10px] opacity-80">{count}</span>
            </button>
          );
        })}
        {(selectedStatuses?.size ?? 0) > 0 ? (
          <Button size="sm" variant="ghost" onClick={onResetStatuses}>
            Reset
          </Button>
        ) : null}
      </div>
    </>
  );

  if (embedded) return content;

  return (
    <Card>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
